import React, { useState, useEffect, useMemo, Fragment } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from './Header';
import { FaShippingFast } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { getTranslation, Language } from '../translations';
import Typed from 'typed.js';
import HowItWorksModal from './HowItWorksModal';
import { motion } from 'framer-motion';
import { Smartphone, Tablet, Watch, Monitor, Laptop, Gamepad2, ChevronRight, Clock } from 'lucide-react';
import { getAllDeviceTypes, DeviceTypeConfig, DeviceCategory } from '../config/deviceConfig';
import { getPublicDeviceTypes, type PublicDeviceType } from '../lib/publicDataAccess';

// Animation keyframes
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const typewriter = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const blinkCursor = keyframes`
  0%, 50% {
    border-right-color: transparent;
  }
  51%, 100% {
    border-right-color: #374151;
  }
`;

const blinkCursorDark = keyframes`
  0%, 50% {
    border-right-color: transparent;
  }
  51%, 100% {
    border-right-color: #e2e8f0;
  }
`;

// Safe icon renderer to avoid JSX typing issues with react-icons in TS
const renderIcon = (Icon: IconType, props?: any) => {
  return (Icon ? (React.createElement(Icon as any, props)) : null);
};

const slideIn = keyframes`
  0% {
    transform: translateY(60%);
    opacity: 0;
  }
  20% {
    transform: translateY(0%);
    opacity: 1;
  }
  80% {
    transform: translateY(0%);
    opacity: 1;
  }
  100% {
    transform: translateY(-40%);
    opacity: 0;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
`;

const bounceArrow = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0,-6px,0);
  }
  70% {
    transform: translate3d(0,-3px,0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.2);
  }
`;

const PremiumHeroWrapper = styled.div`
  position: relative;
  width: 100vw;
  min-height: 100vh;
  margin: 0;
  margin-top: -80px;
  padding: 0;
  padding-top: 140px;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  overflow: hidden;
  transition: background 0.3s ease;

  .dark & {
    background: linear-gradient(135deg, #0a0c10 0%, #0e1116 100%);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.02;
    background-image:
      linear-gradient(90deg, #475569 1px, transparent 1px),
      linear-gradient(180deg, #475569 1px, transparent 1px);
    background-size: 32px 32px;
    z-index: 0;

    .dark & {
      opacity: 0.06;
      background-image:
        linear-gradient(90deg, #22272f 1px, transparent 1px),
        linear-gradient(180deg, #22272f 1px, transparent 1px);
    }
  }

  @media (max-width: 768px) {
    height: calc(100svh + 80px);
    min-height: 0;
    margin-top: -80px;
    padding-top: 80px;
    padding-bottom: 0.75rem;
    box-sizing: border-box;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }
`;

const PremiumContainer = styled.div`
  position: relative;
  z-index: 10;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  
  /* Ensure content is above background effects */
  & > * {
    position: relative;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    padding: 0 0.75rem;
    margin-top: 0;
    flex: 1;
    justify-content: flex-start;
    height: auto;
  }
`;

// Simple top toggle (Repareren / Verkopen)
const ToggleBar = styled.div`
  display: inline-flex;
  background: #f8fafc;
  padding: 4px;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;

  .dark & {
    background: #1e293b;
    border-color: #334155;
  }
`;

const ToggleButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
}) <{ active?: boolean }>`
  padding: 0.6rem 1.5rem;
  border-radius: 9999px;
  border: ${p => p.active ? '1.5px solid #0f172a' : '1.5px solid transparent'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  background: ${p => p.active ? '#ffffff' : 'transparent'};
  color: ${p => p.active ? '#0f172a' : '#64748b'};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: #0f172a;
  }

  .dark & {
    background: ${p => p.active ? '#0f172a' : 'transparent'};
    color: ${p => p.active ? '#ffffff' : '#94a3b8'};
    border-color: ${p => p.active ? '#f8fafc' : 'transparent'};

    &:hover {
      color: #e2e8f0;
    }
  }
`;

// Rows for header pills
const HeaderPillsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const CenteredToggleRow = styled.div`
  display: flex;
  justify-content: center;
  margin: 0.5rem 0 1rem 0;
`;

const AltPanel = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 24px;
  padding: 4rem 2rem;
  text-align: center;
  color: #374151;
  margin-top: 1rem;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);

  .dark & {
    background: #1e293b;
    border-color: #334155;
    color: #cbd5e1;
  }

  @media (max-width: 640px) {
    padding: 2.5rem 1.5rem;
    min-height: auto;
    border-radius: 20px;
    margin-top: 0.5rem;
  }
`;

const AltBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #f1f5f9;
  color: #475569;
  padding: 0.45rem 1rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1.25rem;

  .dark & {
    background: rgba(30, 41, 59, 0.8);
    color: #cbd5e1;
  }
`;

const AltTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 0.75rem 0;
  color: #111827;

  .dark & { color: #f8fafc; }

  @media (max-width: 640px) {
    font-size: 1.35rem;
  }
`;

const AltText = styled.p`
  margin: 0 0 1.5rem 0;
  color: #4b5563;
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;

  .dark & { color: #94a3b8; }

  @media (max-width: 640px) {
    font-size: 0.925rem;
    line-height: 1.55;
    margin-bottom: 1.25rem;
  }
`;

const AltButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  text-decoration: none;
  transition: all 0.25s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(17, 24, 39, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.15);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }

  .dark & {
    background: #f8fafc;
    color: #0f172a;

    &:hover {
      box-shadow: 0 8px 24px rgba(255, 255, 255, 0.15);
    }
  }
`;

// Progress indicator met premium styling
const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
  margin-bottom: 1rem;
  
  @media (min-width: 640px) {
    gap: 4rem;
  }
`;

const StepItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  position: relative;
`;

const StepDot = styled.div<{ $isActive?: boolean }>`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  transition: all 0.4s ease;
  position: relative;

  ${props => props.$isActive ? css`
    background: linear-gradient(135deg, #111827, #1f2937);
    color: white;
    box-shadow: 0 8px 25px rgba(17, 24, 39, 0.25);
    transform: scale(1.06);

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(17, 24, 39, 0.2);
      border-radius: 50%;
      filter: blur(1rem);
      scale: 1.4;
      animation: ${pulseGlow} 2s ease-in-out infinite;
    }

    .dark & {
      background: linear-gradient(135deg, #374151, #1f2937);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5);
    }
    .dark &::before {
      background: rgba(148, 163, 184, 0.18);
    }
  ` : css`
    background: white;
    color: #9ca3af;
    border: 2px solid #e5e7eb;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

    .dark & {
      background: #1e293b;
      color: #64748b;
      border: 2px solid #334155;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    }
  `}
`;

const StepIcon = styled.svg`
  width: 1.25rem;
  height: 1.25rem;
  position: relative;
  z-index: 2;
`;

const StepText = styled.span<{ $isActive?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.$isActive ? '#111827' : '#9ca3af'};
  transition: color 0.3s ease;

  .dark & {
    color: ${props => props.$isActive ? '#e5e7eb' : '#94a3b8'};
  }
`;


const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.875rem;
  color: #374151;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  .dark & {
    background: rgba(24, 27, 34, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: #e5e7eb;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const StatusDot = styled.div`
  width: 0.375rem;
  height: 0.375rem;
  background: #34d399;
  border-radius: 50%;
  margin-right: 0.5rem;
  animation: ${pulseGlow} 2s infinite;
  transition: background 0.3s ease;
  
  .dark & {
    background: #4ade80;
    box-shadow: 0 0 4px rgba(74, 222, 128, 0.4);
  }
`;

// Hero content met premium typography
const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;
  width: 100%;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    gap: 4rem;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.75rem;
  text-align: left;
  max-width: 34rem;

  @media (max-width: 1024px) {
    max-width: 100%;
    align-items: center;
    text-align: center;
  }

  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
    padding: 0;
    flex: 1;
    justify-content: center;
    gap: clamp(1rem, 3vh, 1.75rem);
  }
`;

const HeroBadge = styled.div`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  color: #374151;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  .dark & {
    background: #1f2937;
    color: #e5e7eb;
    border-color: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 1024px) {
    align-self: center;
  }

`;

const HeroBadgeDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
`;

const HeroBadgeText = styled.span`
  letter-spacing: 0.06em;
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 1024px) { justify-content: center; }

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 0.6rem;
  }
`;

const PrimaryActionButton = styled.button`
  appearance: none;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 1.125rem 2.5rem;
  border-radius: 14px;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  box-shadow: 0 4px 16px rgba(17, 24, 39, 0.25);
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.35);
  }

  .dark & {
    background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
    color: #111827;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 2rem;
    font-size: 1rem;
    border-radius: 14px;
  }
`;

const SecondaryActionButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.75rem;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-2px);
  }

  .dark & {
    color: #cbd5e1;
    background: rgba(30, 41, 59, 0.8);
    border-color: rgba(71, 85, 105, 0.5);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
    border-radius: 14px;
  }
`;

const SecondaryActionIcon = styled.svg`
  width: 1.125rem;
  height: 1.125rem;
  stroke-width: 2.5px;
`;

const HeroCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.8rem;
  padding: 2.6rem;
  border-radius: 36px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 40px 70px rgba(15, 23, 42, 0.14);
  backdrop-filter: blur(14px);

  .dark & {
    background: rgba(15, 23, 42, 0.62);
    border-color: rgba(148, 163, 184, 0.24);
  }

  @media (max-width: 1024px) { display: none; }
`;

const HeroCardBadge = styled.div`
  padding: 0.45rem 0.9rem;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.06);
  color: #1f2937;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;

  .dark & {
    background: rgba(30, 41, 59, 0.7);
    color: #e2e8f0;
  }
`;

const HeroCardTitle = styled.h3`
  font-size: 2.05rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  text-align: center;
  .dark & { color: #f8fafc; }
`;

const HeroCardSubtitle = styled.p`
  font-size: 1.02rem;
  line-height: 1.65;
  color: #475569;
  max-width: 28rem;
  margin: 0;
  text-align: center;
  .dark & { color: #cbd5e1; }
`;

const HeroCardDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, rgba(148, 163, 184, 0.28) 0%, rgba(100, 116, 139, 0.4) 50%, rgba(148, 163, 184, 0.28) 100%);
`;

const HeroCardFooter = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.6rem 1.15rem;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.06);
  color: #1f2937;
  font-size: 0.88rem;
  font-weight: 600;
  border: 1px solid rgba(15, 23, 42, 0.08);
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 22px rgba(15, 23, 42, 0.08);
  }

  .dark & {
    background: rgba(30, 41, 59, 0.72);
    color: #e2e8f0;
    border: 1px solid rgba(71, 85, 105, 0.32);
  }
`;

const HeroCardFooterIcon = styled.svg`
  width: 0.95rem;
  height: 0.95rem;
  stroke-width: 2.2px;
`;

// Assistive text for animated titles
const VisuallyHidden = styled.span`
  position: absolute !important;
  height: 1px; width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
`;

const MainTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0 0 1.25rem 0;
  line-height: 1.15;
  color: #111827;

  @media (min-width: 640px) { font-size: 2.75rem; }
  @media (min-width: 1024px) { font-size: 3.25rem; text-align: left; }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin: 0 0 0.75rem 0;
  }

  .dark & { color: #f3f4f6; }
`;

const TitlePart1 = styled.span`
  color: #111827;
  display: inline-block;

  .dark & {
    color: #f3f4f6;
  }
`;

const TitlePart2 = styled.span`
  color: #1f2937;
  display: block;
  position: relative;
  margin-top: 0.35rem;
  line-height: 1.15;
  min-height: 1.4em;
  height: auto;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;

  .dark & { color: #e5e7eb; }

  &::before {
    content: '\200b';
    display: inline-block;
  }

  .typed-cursor {
    font-weight: 400;
    color: #2563eb;
    font-size: 0.95em;
    opacity: 1;
    animation: blink 0.75s infinite;
    margin-left: 4px;
    display: inline-block;
    vertical-align: baseline;
  }

  @keyframes blink {
    0% { opacity: 1; }
    50% { opacity: 0; }
    100% { opacity: 1; }
  }
`;

const TypedWrapper = styled.span`
  display: inline-block;
  min-width: 10ch;
  white-space: nowrap;
`;

const TypewriterText = styled.span<{ isTyping?: boolean; textLength?: number }>`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  border-right: 3px solid #374151;
  
  .dark & {
    border-right: 3px solid #e2e8f0;
  }
  
  ${props => props.isTyping && css`
    animation: 
      ${typewriter} 2s steps(${props.textLength || 20}, end) forwards,
      ${blinkCursor} 1s step-end infinite;
    width: 0;
  `}
  
  ${props => !props.isTyping && css`
    border-right: 3px solid transparent;
    animation: ${blinkCursor} 1s step-end infinite;
    animation-delay: 2.2s;
  `}
`;

const RollingTextSpan = styled.span<{ textKey: string }>`
  position: absolute;
  left: 0;
  top: 0;
  display: inline-block;
  white-space: nowrap;
  transform: none;
  color: #1f2937;
  padding: 0.15rem 0;
  animation: ${slideIn} 2400ms ease-in-out infinite;

  /* Respect reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  .dark & {
    color: #e5e7eb;
  }
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: #4b5563;
  max-width: 34rem;
  margin: 0 0 1.75rem 0;
  line-height: 1.65;
  font-weight: 500;

  .dark & { color: #94a3b8; }

  @media (max-width: 1024px) { text-align: center; }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 0 0 1rem 0;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    margin: 0 0 0.75rem 0;
  }
`;

const SubtitleHighlight = styled.span`
  color: #374151;
  font-weight: 600;
  transition: color 0.3s ease;
  
  .dark & {
    color: #e2e8f0;
  }
`;

const ValuePoints = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.8rem 1.2rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;

  @media (max-width: 1024px) { justify-content: center; }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }
`;

const ValuePoint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.95rem;
  color: #0f172a;
  font-weight: 600;
  padding: 0.6rem 0.95rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.08);

  .dark & {
    color: #e5e7eb;
    background: rgba(17, 24, 39, 0.78);
    border-color: rgba(148, 163, 184, 0.24);
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 0.45rem 0.7rem;
    border-radius: 10px;
    gap: 0.4rem;
  }
`;

const ValueIcon = styled.svg<{ color: string }>`
  width: 18px;
  height: 18px;
  color: ${props => props.color};
`;

const ValueIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(15, 23, 42, 0.12);

  .dark & {
    background: rgba(30, 41, 59, 0.75);
    border-color: rgba(71, 85, 105, 0.4);
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    border-radius: 10px;
  }
`;


const ActionPrompt = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1.1rem;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.95rem;
  color: #374151;
  font-weight: 600;
  margin-bottom: 1.25rem;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  
  .dark & {
    background: rgba(17, 24, 39, 0.6);
    border: 1px solid #2a2f3a;
    color: #e5e7eb;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  }

  @media (max-width: 520px) {
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 480px) {
    padding: 0.6rem 0.9rem;
    font-size: 0.85rem;
    border-radius: 0.875rem;
  }
`;

const QuickStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.9rem;
  width: 100%;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(37, 99, 235, 0.12);
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.08);

  .dark & {
    background: rgba(15, 23, 42, 0.75);
    border-color: rgba(59, 130, 246, 0.2);
    color: #e5e7eb;
  }
`;

const StatNumber = styled.span`
  font-size: 1.4rem;
  font-weight: 800;
  color: inherit;
`;

const StatLabel = styled.span`
  font-size: 0.9rem;
  color: #475569;
  font-weight: 600;
  .dark & { color: #94a3b8; }
`;

const StatSeparator = styled.span`
  display: none;
`;

const ActionIconWrap = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #eef4ff 0%, #f7fbff 100%);
  border: 1px solid rgba(0,0,0,0.04);
`;

const ActionIcon = styled.svg`
  width: 14px;
  height: 14px;
  color: #374151;
  transition: color 0.3s ease;
  
  .dark & {
    color: #cbd5e1;
  }
`;

const DownArrow = styled.div``;

const scrollDot = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(8px);
    opacity: 0;
  }
`;

const ScrollIndicator = styled.button`
  display: none;
  appearance: none;
  border: none;
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0;
  margin: 0 auto;
  flex-shrink: 0;
  background: transparent;
  color: #9ca3af;
  transition: color 0.2s ease;

  .dark & {
    color: #64748b;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const ScrollMouse = styled.div`
  width: 22px;
  height: 34px;
  border-radius: 11px;
  border: 2px solid currentColor;
  position: relative;
  opacity: 0.5;

  &::after {
    content: '';
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 3px;
    height: 6px;
    border-radius: 3px;
    background: currentColor;
    animation: ${scrollDot} 1.5s ease-in-out infinite;
  }
`;

const ArrowIcon = styled.svg`
  width: 1.25rem;
  height: 1.25rem;
  color: #9ca3af;
  animation: ${bounceArrow} 2s infinite;
  transition: color 0.3s ease;
  
  .dark & {
    color: #cbd5e1;
  }
`;

// Device Section styling
const DeviceSection = styled.div`
  position: relative;
  padding: clamp(4.5rem, 6vw, 6.25rem) 0 clamp(4rem, 5vw, 5.5rem);

  @media (max-width: 768px) {
    padding: 1.5rem 0 2rem;
  }
`;

const DeviceSectionContainer = styled.div`
  position: relative;
  padding: clamp(2.6rem, 4vw, 3.5rem) clamp(1.8rem, 3.2vw, 3rem);
  border-radius: 36px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1);

  .dark & {
    background: #0f172a;
    border-color: rgba(71, 85, 105, 0.45);
  }

  @media (max-width: 768px) {
    padding: 1.25rem 0.875rem;
    border-radius: 18px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
`;

const DeviceSectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
`;

const TrustBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    flex-shrink: 0;
    display: block;
    position: relative;
  }

  span {
    position: relative;
    letter-spacing: 0.06em;
  }

  &:hover {
    background: #f9fafb;
    border-color: rgba(17, 24, 39, 0.12);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  .dark & {
    background: #1f2937;
    color: #e5e7eb;
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

    &::before {
      background: #10b981;
    }

    &:hover {
      background: #374151;
      border-color: rgba(255, 255, 255, 0.15);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    }
  }

  @media (max-width: 768px) {
    padding: 0.4375rem 0.875rem;
    font-size: 0.6875rem;
    gap: 0.4375rem;

    &::before {
      width: 5px;
      height: 5px;
    }
  }

  @media (max-width: 480px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.625rem;
    gap: 0.375rem;

    &::before {
      width: 4px;
      height: 4px;
    }
  }
`;

const TrustIcon = styled.svg`
  display: none;
`;

const TrustText = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: #047857;
  letter-spacing: 0.08em;
  transition: color 0.3s ease;

  .dark & {
    color: #34d399;
  }
`;

const DeviceTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 1rem;

  .dark & { color: #f8fafc; }

  @media (max-width: 480px) {
    font-size: 1.6rem;
    margin-bottom: 0.5rem;
  }
`;

const DeviceSubtitle = styled.p`
  font-size: 1.125rem;
  color: #4b5563;
  max-width: 600px;
  margin: 0 auto 2rem;

  .dark & { color: #94a3b8; }

  @media (max-width: 480px) {
    font-size: 0.925rem;
    margin: 0 auto 1rem;
  }
`;

const DeviceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const DeviceCard = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected',
}) <{ isSelected?: boolean }>`
  position: relative;
  background: #ffffff;
  border-radius: 24px;
  padding: 2.5rem 2rem;
  border: ${p => p.isSelected ? '2px solid #111827' : '1px solid #f1f5f9'};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  height: 100%;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.08);
    border-color: #111827;
  }

  .dark & {
    background: #1e293b;
    border-color: ${p => p.isSelected ? '#3b82f6' : '#334155'};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);

    &:hover {
      background: #262f45;
      box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.4);
    }
  }

  @media (max-width: 640px) {
    padding: 1.25rem 1rem;
    border-radius: 18px;
  }
`;

const DeviceImage = styled.img`
  height: 72px;
  max-height: 72px;
  object-fit: contain;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  @media (max-width: 640px) {
    height: 52px;
    max-height: 52px;
    margin-bottom: 0.75rem;
  }
`;

const DeviceName = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;

  .dark & { color: #f8fafc; }

  @media (max-width: 640px) {
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #111827;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  z-index: 2;
`;

const DeviceTooltip = styled.div`
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;

  .dark & { color: #94a3b8; }

  @media (max-width: 640px) {
    font-size: 0.8rem;
    line-height: 1.35;
  }
`;


const SkeletonCard = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 24px;
  padding: 2.5rem 2rem;
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;

  .dark & {
    background: #1e293b;
    border-color: #334155;
  }
`;

const SkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 1rem;
`;

const SkeletonBase = styled.div`
  background: #e2e8f0;
  background-image: linear-gradient(to right, #e2e8f0 0%, #f1f5f9 20%, #e2e8f0 40%, #e2e8f0 100%);
  background-repeat: no-repeat;
  background-size: 2000px 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 6px;

  .dark & {
    background: #1e293b;
    background-image: linear-gradient(to right, #1e293b 0%, #334155 20%, #1e293b 40%, #1e293b 100%);
  }
`;

const SkeletonImage = styled(SkeletonBase)`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const SkeletonTitle = styled(SkeletonBase)`
  width: 70%;
  height: 1.2rem;
`;

const SkeletonSubtitle = styled(SkeletonBase)`
  width: 40%;
  height: 0.9rem;
`;

// removed compact popover styles

type DeviceType = string; // Nu flexibel - geen hardcoded types meer

/** Resolve a multilingual JSONB label to the best available string */
function resolveLabel(obj: Record<string, string> | undefined | null, lang: string): string {
  if (!obj) return '';
  return obj[lang] || obj.nl || obj.en || Object.values(obj)[0] || '';
}

/** Vertaalsleutel per toesteltype voor de korte hint (sleutelnamen wijken af van de id, bv. 'Samsung' -> 'samsungHint') */
const DEVICE_HINT_KEYS: Record<string, string> = {
  iphone: 'iPhoneHint',
  samsung: 'samsungHint',
  ipad: 'iPadHint',
  macbook: 'macBookHint',
  imac: 'iMacHint',
  'apple watch': 'appleWatchHint',
  'apple-watch': 'appleWatchHint',
  apple_watch: 'appleWatchHint',
};

const getDeviceHint = (deviceId: string, t: (key: string) => string): string => {
  const key = DEVICE_HINT_KEYS[deviceId.toLowerCase()];
  return key ? t(key) : '';
};

/** Static fallback when Supabase is unavailable (names from config; hints translated) */
const getDevicesFallback = (t: (key: string) => string): Array<{
  id: string;
  name: string;
  image: string;
  isPopular?: boolean;
  hint: string;
  category: DeviceCategory;
}> => {
  const deviceTypes = getAllDeviceTypes();
  return deviceTypes.map(device => ({
    id: device.id,
    name: device.name,
    image: device.image,
    isPopular: device.isPopular,
    hint: getDeviceHint(device.id, t) || device.hint || '',
    category: device.category,
  }));
};

interface DeviceSelectionProps {
  onDeviceTypeSelect: (deviceId: string) => void;
}

// RollingText component
const RollingText: React.FC<{ language: string; overrideTitle?: string | null }> = ({ language, overrideTitle }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const typedRef = React.useRef<HTMLSpanElement | null>(null);
  const typedInstance = React.useRef<Typed | null>(null);
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const textsByLanguage: Record<string, string[]> = {
    nl: ['voor wat je wilt', 'eenvoudig en snel', 'zonder gedoe', 'voor een upgrade'],
    en: ['made simple', 'fast and easy', 'no hassle', 'for an upgrade'],
    fr: ['simple et rapide', 'sans tracas'],
    de: ['einfach und schnell'],
    es: ['rápido y sencillo'],
    tr: ['kolay ve hızlı'],
    bg: ['лесно и бързо'],
    ro: ['simplu și rapid'],
    sk: ['jednoducho a rýchlo']
  };

  const titlesByLanguage: Record<string, string> = {
    nl: 'Ruil in wat je hebt',
    en: 'Trade in what you have',
    fr: 'Échangez ce que vous avez',
    de: 'Tausche ein, was du hast',
    es: 'Intercambia lo que tienes',
    tr: 'Sahip olduğunuzu takas edin',
    bg: 'Разменете това, което имате',
    ro: 'Schimbați ceea ce aveți',
    sk: 'Vymeňte to, čo máte'
  };

  const baseTitle = titlesByLanguage[language] || titlesByLanguage.nl;
  const title = overrideTitle || baseTitle;
  const [first, secondFallback] = title.split('\n');

  useEffect(() => {
    if (!typedRef.current || overrideTitle || prefersReduced || typeof window === 'undefined') return;

    const strings = textsByLanguage[language] || textsByLanguage.nl;
    typedInstance.current = new Typed(typedRef.current, {
      strings,
      typeSpeed: 60,
      backSpeed: 40,
      backDelay: 1200,
      startDelay: 200,
      loop: true,
      smartBackspace: false,
      showCursor: true,
      cursorChar: '▏',
      fadeOut: false,
      fadeOutDelay: 0,
      shuffle: false,
      autoInsertCss: false
    });

    return () => {
      if (typedInstance.current) {
        typedInstance.current.destroy();
        typedInstance.current = null;
      }
    };
  }, [language, overrideTitle, prefersReduced]);

  useEffect(() => {
    if (overrideTitle) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % 1000); // inert; typed.js regelt content
    }, 5000);
    return () => clearInterval(interval);
  }, [overrideTitle]);

  return (
    <MainTitle>
      <TitlePart1>{first}</TitlePart1>
      <TitlePart2>
        {overrideTitle ? (
          <TypedWrapper>{secondFallback}</TypedWrapper>
        ) : (
          <TypedWrapper>
            <span ref={typedRef} />
          </TypedWrapper>
        )}
      </TitlePart2>
    </MainTitle>
  );
};

// Helper: device-specifieke koptekst (vertaald; eerste regel bevat het toestel)
const getDeviceHeroTitle = (device: string | null, t: (key: string) => string): string | null => {
  if (!device) return null;
  const deviceType = getAllDeviceTypes().find(d => d.id === device);
  return deviceType
    ? `${t('sellDeviceHeroTitle').replace('{device}', deviceType.name)}\n${t('sellDeviceHeroSubtitle')}`
    : null;
};

// Safe icon renderer
const renderLucideIcon = (Icon: any, props?: any) => {
  return (Icon ? (React.createElement(Icon, { ...props, strokeWidth: 1.5 })) : null);
};

// Category icon mapping for device types
const categoryIconMap: Record<string, typeof Smartphone> = {
  phone: Smartphone,
  smartphone: Smartphone,
  tablet: Tablet,
  watch: Watch,
  smartwatch: Watch,
  desktop: Monitor,
  laptop: Laptop,
  console: Gamepad2,
  other: Smartphone,
};

// Card Grid (matching Repair)
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const Card = styled.div<{ $isPopular?: boolean; $isComingSoon?: boolean }>`
  position: relative;
  background: ${props => props.$isComingSoon
    ? 'linear-gradient(180deg, #fafbfc 0%, #f4f6f9 100%)'
    : '#ffffff'};
  border-radius: 24px;
  padding: 2.5rem 2rem;
  border: ${props => {
    if (props.$isComingSoon) return '1px dashed #cbd5e1';
    return props.$isPopular ? '2px solid #111827' : '1px solid #f1f5f9';
  }};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${props => props.$isComingSoon ? 'not-allowed' : 'pointer'};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  height: 100%;
  overflow: hidden;

  &:hover {
    transform: ${props => props.$isComingSoon ? 'none' : 'translateY(-8px)'};
    box-shadow: ${props => props.$isComingSoon
      ? '0 4px 6px -1px rgba(0, 0, 0, 0.02)'
      : '0 20px 40px -5px rgba(0, 0, 0, 0.08)'};
    border-color: ${props => {
      if (props.$isComingSoon) return '#cbd5e1';
      return '#111827';
    }};
  }

  .dark & {
    background: ${props => props.$isComingSoon
      ? 'linear-gradient(180deg, #1e293b 0%, #1a2333 100%)'
      : '#1e293b'};
    border-color: ${props => {
      if (props.$isComingSoon) return '#475569';
      return props.$isPopular ? '#3b82f6' : '#334155';
    }};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2);

    &:hover {
      background: ${props => props.$isComingSoon
        ? 'linear-gradient(180deg, #1e293b 0%, #1a2333 100%)'
        : '#262f45'};
      box-shadow: ${props => props.$isComingSoon
        ? '0 4px 6px -1px rgba(0, 0, 0, 0.2)'
        : '0 20px 40px -5px rgba(0, 0, 0, 0.4)'};
    }
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
    border-radius: 18px;
  }
`;

// Pill in the top-right corner, replaces the popular tag for coming-soon items.
const ComingSoonRibbon = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  z-index: 2;
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.18);

  svg {
    width: 12px;
    height: 12px;
  }

  .dark & {
    background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  }

  @media (max-width: 768px) {
    top: 0.625rem;
    right: 0.625rem;
    font-size: 0.6rem;
    padding: 0.3rem 0.6rem;

    svg { width: 10px; height: 10px; }
  }
`;

// Replaces CardButton on coming-soon cards. Clearly signals "not yet available"
// without hiding what device this card represents.
const ComingSoonButton = styled.div`
  width: 100%;
  padding: 0.875rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px dashed #94a3b8;
  background: rgba(241, 245, 249, 0.6);
  color: #64748b;
  cursor: not-allowed;
  user-select: none;

  svg { width: 16px; height: 16px; }

  .dark & {
    border-color: #475569;
    background: rgba(51, 65, 85, 0.4);
    color: #94a3b8;
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 12px;
    font-size: 0.85rem;
  }
`;


// Device-type IDs that show a "coming soon" overlay (case-insensitive
// to handle both Supabase keys "imac"/"macbook" and legacy fallback "iMac"/"MacBook").
const COMING_SOON_TYPE_IDS = new Set(['imac', 'macbook']);
const isComingSoonDevice = (id: string) => COMING_SOON_TYPE_IDS.has(id.toLowerCase());

const CardIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: #f8fafc;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  ${Card}:hover & {
    background: #111827;
    color: #ffffff;
  }

  .dark & {
    background: #1e293b;
    color: #94a3b8;

    ${Card}:hover & {
      background: rgba(37, 99, 235, 0.2);
      color: #60a5fa;
    }
  }

  svg {
    width: 36px;
    height: 36px;
  }

  @media (max-width: 768px) {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    margin-bottom: 0.75rem;

    svg {
      width: 26px;
      height: 26px;
    }
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;

  .dark & { color: #f8fafc; }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }
`;

const PopularTag = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #111827;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  z-index: 2;

  @media (max-width: 768px) {
    top: 0.625rem;
    right: 0.625rem;
    font-size: 0.6rem;
    padding: 0.25rem 0.5rem;
  }
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.5;

  .dark & { color: #94a3b8; }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const CardButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: 0.875rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;

  background: ${props => props.$active ? '#111827' : '#f1f5f9'};
  color: ${props => props.$active ? '#ffffff' : '#475569'};

  svg {
    transition: transform 0.2s ease;
  }

  ${Card}:hover & {
    background: #111827;
    color: #ffffff;
    transform: translateY(2px);
  }

  &:hover svg, ${Card}:hover & svg {
    transform: translateX(4px);
  }

  .dark & {
    background: ${props => props.$active ? '#3b82f6' : '#334155'};
    color: ${props => props.$active ? '#ffffff' : '#94a3b8'};

    ${Card}:hover & {
      background: #3b82f6;
      color: #ffffff;
    }
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 12px;
    font-size: 0.85rem;
  }
`;

const CardDeviceImage = styled.img`
  height: 72px;
  max-height: 72px;
  object-fit: contain;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 52px;
    max-height: 52px;
    margin-bottom: 0.75rem;
  }
`;

// Toggle (matching Repair)
const ToggleContainer = styled.div`
  display: inline-flex;
  background: #f8fafc;
  padding: 4px;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
  margin-bottom: 2rem;

  .dark & {
    background: #1e293b;
    border-color: #334155;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
  }
`;

const ToggleBtn = styled.button<{ $active?: boolean }>`
  padding: 0.6rem 1.5rem;
  border-radius: 9999px;
  border: ${props => props.$active ? '1.5px solid #0f172a' : '1.5px solid transparent'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  background: ${props => props.$active ? '#ffffff' : 'transparent'};
  color: ${props => props.$active ? '#0f172a' : '#64748b'};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: #0f172a;
  }

  .dark & {
    background: ${props => props.$active ? '#0f172a' : 'transparent'};
    color: ${props => props.$active ? '#ffffff' : '#94a3b8'};
    border-color: ${props => props.$active ? '#f8fafc' : 'transparent'};

    &:hover {
      color: #e2e8f0;
    }
  }
`;

// Sales Promo Panel (matching Repair's SalesPromoPanel)
const SalesPromoPanel = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 24px;
  padding: 4rem 2rem;
  text-align: center;
  color: #374151;
  margin-top: 1rem;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);

  .dark & {
    background: #1e293b;
    border-color: #334155;
    color: #cbd5e1;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
    min-height: auto;
    border-radius: 20px;
    margin-top: 0.5rem;
  }
`;

const SalesPromoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #f1f5f9;
  color: #475569;
  padding: 0.45rem 1rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1.25rem;

  .dark & {
    background: rgba(30, 41, 59, 0.8);
    color: #cbd5e1;
  }
`;

const SalesPromoTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 0.75rem 0;
  color: #111827;

  .dark & { color: #f8fafc; }

  @media (max-width: 768px) {
    font-size: 1.35rem;
  }
`;

const SalesPromoText = styled.p`
  margin: 0 0 1.5rem 0;
  color: #4b5563;
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;

  .dark & { color: #94a3b8; }

  @media (max-width: 768px) {
    font-size: 0.925rem;
    line-height: 1.55;
    margin-bottom: 1.25rem;
  }
`;

const SalesPromoButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  text-decoration: none;
  transition: all 0.25s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(17, 24, 39, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.15);
  }

  svg {
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }

  .dark & {
    background: #f8fafc;
    color: #0f172a;

    &:hover {
      box-shadow: 0 8px 24px rgba(255, 255, 255, 0.15);
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
  }
`;

const DeviceSelection: React.FC<DeviceSelectionProps> = ({ onDeviceTypeSelect }) => {
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [mode, setMode] = useState<'sell' | 'repair'>('sell');
  const { language } = useLanguage();
  const t = (key: any) => getTranslation(language as Language, key);
  const REPAIR_PLUGIN_URL = (typeof window !== 'undefined' && (window as any).REPAIR_PLUGIN_URL) || 'https://repairmodule.phonebros.be/';
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Fetch device types from Supabase (raw JSONB) — language resolved at render time
  const [supabaseDeviceTypes, setSupabaseDeviceTypes] = useState<PublicDeviceType[]>([]);
  useEffect(() => {
    getPublicDeviceTypes()
      .then(setSupabaseDeviceTypes)
      .catch(() => setSupabaseDeviceTypes([]));
  }, []);

  // Resolve multilingual labels for current language
  const devices = useMemo(() => {
    if (supabaseDeviceTypes.length > 0) {
      return supabaseDeviceTypes.map(device => ({
        id: device.id,
        name: resolveLabel(device.name, language),
        image: device.image,
        isPopular: device.isPopular,
        isComingSoon: isComingSoonDevice(device.id),
        hint: resolveLabel(device.hint, language) || getDeviceHint(device.id, t) || '',
        category: device.category,
      }));
    }
    return getDevicesFallback(t).map(d => ({ ...d, isComingSoon: isComingSoonDevice(d.id) }));
  }, [supabaseDeviceTypes, language, t]);

  const handleDeviceClick = (deviceId: string) => {
    if (isComingSoonDevice(deviceId)) return;
    setSelectedDevice(deviceId);
    setTimeout(() => {
      onDeviceTypeSelect(deviceId);
    }, 300); // Short delay for visual feedback
  };

  const handleScrollToDevices = () => {
    if (typeof window === 'undefined') return;
    const section = document.getElementById('device-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <PremiumHeroWrapper>
        <PremiumContainer>
          <HeroContent>
            <HeroCopy>
              <HeroBadge>
                <HeroBadgeDot />
                <HeroBadgeText>{t('safeAndReliable')}</HeroBadgeText>
              </HeroBadge>

              <RollingText language={language} overrideTitle={getDeviceHeroTitle(selectedDevice, t)} />

              <Subtitle>
                {t('selectDeviceSubtitle')}
              </Subtitle>

              <ValuePoints>
                <ValuePoint>
                  <ValueIconWrap>
                    <ValueIcon color="#16a34a" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </ValueIcon>
                  </ValueIconWrap>
                  <span>{t('step3Description')}</span>
                </ValuePoint>
                <ValuePoint>
                  <ValueIconWrap>
                    <ValueIcon color="#3b82f6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </ValueIcon>
                  </ValueIconWrap>
                  <span>{t('step2Title')}</span>
                </ValuePoint>
                <ValuePoint>
                  <ValueIconWrap>
                    {renderIcon(FaShippingFast as unknown as IconType, { style: { width: 18, height: 18, color: '#64748b' } })}
                  </ValueIconWrap>
                  <span>{t('step1Title')}</span>
                </ValuePoint>
              </ValuePoints>

              <HeroActions>
                <PrimaryActionButton type="button" onClick={handleScrollToDevices}>
                  {t('startSelling')}
                </PrimaryActionButton>
                <SecondaryActionButton type="button" onClick={() => setShowHowItWorks(true)}>
                  <SecondaryActionIcon fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </SecondaryActionIcon>
                  {t('howItWorks')}
                </SecondaryActionButton>
              </HeroActions>
            </HeroCopy>

            <HeroCard>
              <HeroCardBadge>{t('tradeInTool')}</HeroCardBadge>
              <HeroCardTitle>{t('selectDeviceTitle')}</HeroCardTitle>
              <HeroCardSubtitle>{t('selectDeviceSubtitle')}</HeroCardSubtitle>
              <HeroCardDivider />
              <QuickStats>
                <StatItem>
                  <StatNumber>8.000+</StatNumber>
                  <StatLabel>{t('satisfiedCustomers')}</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>10/10</StatNumber>
                  <StatLabel>{t('averageScore')}</StatLabel>
                </StatItem>
              </QuickStats>
            </HeroCard>
          </HeroContent>
        </PremiumContainer>
      </PremiumHeroWrapper>

      <DeviceSection id="device-section">
        <PremiumContainer>
          <DeviceSectionContainer>
            <DeviceSectionHeader>
              <DeviceTitle>
                {mode === 'repair' ? t('selectDeviceTitleRepair') : t('selectDeviceTitle')}
              </DeviceTitle>
              <DeviceSubtitle>
                {mode === 'repair' ? t('selectDeviceSubtitleRepair') : t('selectDeviceSubtitle')}
              </DeviceSubtitle>

              <ToggleContainer>
                <ToggleBtn $active={mode === 'sell'} onClick={() => setMode('sell')}>
                  {t('sellTab')}
                </ToggleBtn>
                <ToggleBtn $active={mode === 'repair'} onClick={() => setMode('repair')}>
                  {t('repairTab')}
                </ToggleBtn>
              </ToggleContainer>
            </DeviceSectionHeader>

            {mode === 'sell' ? (
              <CardGrid>
                {devices.map((device) => (
                  <Card
                    key={device.id}
                    $isPopular={device.isPopular}
                    $isComingSoon={device.isComingSoon}
                    onClick={() => handleDeviceClick(device.id)}
                    aria-disabled={device.isComingSoon || undefined}
                  >
                    {device.isPopular && !device.isComingSoon && <PopularTag>{t('mostChosen')}</PopularTag>}
                    {device.isComingSoon && (
                      <ComingSoonRibbon>
                        <Clock size={12} /> {t('comingSoon')}
                      </ComingSoonRibbon>
                    )}
                    <CardDeviceImage
                      src={encodeURI(device.image)}
                      alt={device.name}
                      loading="lazy"
                    />
                    <CardTitle>{device.name}</CardTitle>
                    <CardDesc>{device.hint}</CardDesc>
                    {device.isComingSoon ? (
                      <ComingSoonButton aria-disabled="true">
                        <Clock size={16} /> {t('comingSoon')}
                      </ComingSoonButton>
                    ) : (
                      <CardButton $active={device.isPopular}>
                        {t('startSelling')} <ChevronRight size={16} />
                      </CardButton>
                    )}
                  </Card>
                ))}
              </CardGrid>
            ) : (
              <SalesPromoPanel>
                <SalesPromoBadge>{t('repairPromoBadge')}</SalesPromoBadge>
                <SalesPromoTitle>{t('repairPromoTitle')}</SalesPromoTitle>
                <SalesPromoText>{t('repairPromoText')}</SalesPromoText>
                <SalesPromoButton onClick={() => { window.location.href = REPAIR_PLUGIN_URL; }}>
                  {t('repairPromoButton')} <ChevronRight size={20} />
                </SalesPromoButton>
              </SalesPromoPanel>
            )}

          </DeviceSectionContainer>
        </PremiumContainer>
      </DeviceSection>
      <HowItWorksModal isOpen={showHowItWorks} onClose={() => setShowHowItWorks(false)} />
    </>
  );
};

export default DeviceSelection;